import { useState } from "react";
import { useIntl } from "react-intl";
import { Button, Checkbox, Icon, Input, Modal, Table } from "semantic-ui-react";
import {
    deleteExaminationOrganizationByGuid,
    editExaminationOrhanizationsAsync,
    orderExamninationOrganization,
} from "../../api/examinationOrganizations";
import { IExaminationOrganization } from "../../models/examinationOrganization";
import { toast } from "../..";
import { useDispatch } from "react-redux";
import { fetchExaminationOrganizations } from "../../store/examinationOrganizationsReducer";

interface Props {
    examination: IExaminationOrganization;
    examinationCount: number;
    rowIndex: number;
}
export const ExaminationOrganizationsRow = ({ examination, examinationCount, rowIndex }: Props) => {
    const intl = useIntl();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [newExamination, setNewExamination] = useState(examination);
    const dispatch = useDispatch();

    const handleDeleteUser = async (guid: string) => {
        try {
            await deleteExaminationOrganizationByGuid(guid);
            toast(intl.formatMessage({ id: "examinationDeleted" }), true);
        } catch {
            toast(intl.formatMessage({ id: "unableToDeleteExamination" }), false);
        } finally {
            fetchExaminationOrganizations(dispatch);
            setEditModalOpen(false);
        }
    };

    const handleEditExamination = async () => {
        try {
            await editExaminationOrhanizationsAsync(newExamination);
            toast(intl.formatMessage({ id: "examinationEdited" }), true);
        } catch {
            toast(intl.formatMessage({ id: "unableToEditPaymentType" }), false);
        } finally {
            fetchExaminationOrganizations(dispatch);
            setEditModalOpen(false);
        }
    };

    const handleOrderNumber = async (order: boolean, guid: string) => {
        await orderExamninationOrganization(guid, order);
        fetchExaminationOrganizations(dispatch);
    };

    return (
        <>
            <Table.Row>
                <Table.Cell>
                    <Icon
                        name="angle up"
                        link
                        onClick={() => handleOrderNumber(false, newExamination.guid)}
                        disabled={rowIndex === 0 ? true : false}
                    />
                    <Icon
                        name="angle down"
                        link
                        onClick={() => handleOrderNumber(true, newExamination.guid)}
                        disabled={rowIndex === examinationCount - 1 ? true : false}
                    />
                </Table.Cell>
                <Table.Cell>{newExamination.name}</Table.Cell>
                <Table.Cell singleLine>
                    <Checkbox checked={!newExamination.disabled} />
                </Table.Cell>
                <Table.Cell width={1} singleLine>
                    <span className="buttons-container">
                        <Button
                            content={intl.formatMessage({ id: "edit" })}
                            basic
                            color="blue"
                            size="small"
                            onClick={() => setEditModalOpen(true)}
                        />
                        <Button
                            content={intl.formatMessage({ id: "delete" })}
                            negative
                            basic
                            size="small"
                            onClick={() => setDeleteModalOpen(true)}
                        />
                    </span>
                </Table.Cell>
            </Table.Row>
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                dimmer="blurring"
                size="tiny"
            >
                <Modal.Header>{intl.formatMessage({ id: "deleteExamination" })}</Modal.Header>
                <Modal.Actions>
                    <Button
                        negative
                        onClick={() => setDeleteModalOpen(false)}
                        content={intl.formatMessage({ id: "cancel" })}
                    />
                    <Button
                        primary
                        onClick={() => handleDeleteUser(examination.guid)}
                        content={intl.formatMessage({ id: "confirm" })}
                    />
                </Modal.Actions>
            </Modal>
            <Modal
                open={editModalOpen}
                dimmer="blurring"
                closeIcon
                onClose={() => setEditModalOpen(false)}
                size="tiny"
            >
                <Modal.Content className="edit-modal">
                    <form className="add-admin-form">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "examinationOrganization" })}</label>
                            <Input
                                value={newExamination.name}
                                onChange={(e: any) =>
                                    setNewExamination({ ...newExamination, name: e.target.value })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <Checkbox
                                label={intl.formatMessage({ id: "active" })}
                                checked={!newExamination.disabled}
                                onChange={(e, data) => {
                                    setNewExamination({
                                        ...newExamination,
                                        disabled: !data.checked!,
                                    });
                                }}
                            />
                        </section>
                    </form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        content={intl.formatMessage({ id: "examinationTypeEdit" })}
                        disabled={!newExamination.name}
                        primary
                        onClick={handleEditExamination}
                        fluid
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};
